import useTables from '@/hooks/tables/useTables';
import { selectActiveStore } from '@/redux/auth/authSlice';
import { selectSalesItemsLayout } from '@/redux/config/configSlice';
import { selectActiveTicket, selectOpenInvoices, selectShift } from '@/redux/data/dataSlice';
import { useAppSelector } from '@/redux/hooks';
import {
  ClockCircleOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { Badge, Button, Input, Skeleton, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LayoutToggle, { LayoutType } from './LayoutToggle';
import RenderTicketCategory from './RenderTicketCategory';
import SelectCategoryModal from './SelectCategoryModal';

const { Title } = Typography;

const SalesHeader = ({
  searchQuery,
  activeTab,
  handleLayoutChange,
  handleSearchQueryChange,
}: {
  searchQuery: string;
  activeTab: string;
  handleLayoutChange: (layout: LayoutType) => void;
  handleSearchQueryChange: (query: string) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [openCategoryModal, setOpenCategoryModal] = useState(false);

  const shift = useAppSelector(selectShift);
  const activeTicket = useAppSelector(selectActiveTicket);
  // const openedTickets = useAppSelector(selectOpenedTicket);
  const openedInvoices = useAppSelector(selectOpenInvoices);
  const salesItemsLayout = useAppSelector(selectSalesItemsLayout);
  const activeStore = useAppSelector(selectActiveStore);

  const { tables, tablesLoading } = useTables(undefined, activeStore?._id);
  const ticketTable = useMemo(() => {
    return tables?.find((table) => table._id === activeTicket?.tableId);
  }, [activeTicket?.tableId, tables]);

  const handleOpenInvoicesPress = useCallback(() => {
    // navigate('/backlog');
    navigate('/open-invoices');
  }, [navigate]);

  const handleTicketCategoryPress = useCallback(() => {
    setOpenCategoryModal(true);
  }, []);

  if (!shift) {
    return null;
  }

  return (
    <div className="bg-white border-b border-gray-200 px-3 py-1">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Title level={4} className="!text-gray-800 !mb-0 font-bold text-sm flex-shrink-0">
            {t('common:sales')}
          </Title>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              type="text"
              size="small"
              className="flex items-center gap-1.5 text-gray-600 hover:bg-gray-100 transition-all duration-200 px-2 py-1 rounded-lg h-8"
              disabled={!(openedInvoices?.length > 0)}
              onClick={handleOpenInvoicesPress}
              title={`${openedInvoices?.length || 0} ${t('common:pending')}`}
            >
              <ShoppingCartOutlined className="text-sm" />
              {openedInvoices?.length > 0 && (
                <Badge
                  count={openedInvoices.length}
                  className="!m-0"
                  style={{ backgroundColor: '#ff4d4f', fontSize: '9px' }}
                />
              )}
            </Button>

            <div className="h-5 w-px bg-gray-300" />

            <div className="flex items-center gap-1.5 text-gray-600">
              <ClockCircleOutlined className="text-xs" />
              <span className="text-[9px] font-medium whitespace-nowrap">
                {new Date(shift.startDate).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-gray-600 ml-10">
              <TagsOutlined className="text-xs" />
              <span
                className="text-[9px] font-medium whitespace-nowrap cursor-pointer hover:text-blue-500 transition-all duration-200"
                onClick={handleTicketCategoryPress}
              >
                <RenderTicketCategory category={activeTicket?.category} />
              </span>
              {tablesLoading ? (
                <Skeleton active />
              ) : ticketTable ? (
                <span className="text-[9px] font-medium whitespace-nowrap">
                  {ticketTable?.name}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Search Bar in Header */}
        {activeTab === 'ITEMS' && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="headerSearch" style={{ maxWidth: '240px' }}>
              <Input
                id="item-search"
                placeholder={t('common:search_items')}
                prefix={<SearchOutlined className="text-gray-400 text-[10px]" />}
                value={searchQuery}
                onChange={(e) => handleSearchQueryChange(e.target.value)}
                className="h-7 text-[11px]"
                allowClear
              />
            </div>
            <LayoutToggle layout={salesItemsLayout} onChange={handleLayoutChange} />
          </div>
        )}
      </div>
      <SelectCategoryModal open={openCategoryModal} onCancel={() => setOpenCategoryModal(false)} />
    </div>
  );
};

export default SalesHeader;
